import { Link } from "react-router-dom";
import { ArrowRight, Package } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useTrackPageView, track } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import BookCover from "@/components/books/BookCover";
import BuyButton from "@/components/books/BuyButton";
import PrintInterestButton from "@/components/books/PrintInterestButton";
import StickyBuyBar from "@/components/books/StickyBuyBar";
import { CYBER_BOOKS, CYBER_BUNDLE, CYBER_SAFETY_ACCENT as C } from "@/data/cyberSafetyBooks";

export default function CyberSafetyBundle() {
  useTrackPageView("cyber_bundle_view", { source: "direct" });
  useSEO({
    title: `${CYBER_BUNDLE.title} — Complete Series · LifeIQ`,
    description: CYBER_BUNDLE.description,
    canonicalPath: "/books/cyber-safety/complete-series",
  });

  return (
    <main>
      <section className="relative overflow-hidden" style={{ backgroundColor: C.navy }}>
        <span aria-hidden="true" className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${C.gold}22` }} />
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-16 md:pt-20 pb-24 md:pb-32 relative">
          <HeroFade delay={0}>
            <div className="flex items-center gap-2 text-[12px] text-white/50 flex-wrap">
              <Link to="/books/cyber-safety" className="hover:text-white transition-colors">Cyber Fraud & Scam Safety</Link>
              <span>/</span>
              <span className="text-white/80">Complete Series</span>
            </div>
          </HeroFade>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <HeroFade delay={0.1}>
                <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>
                  <Package size={14} /> Complete series
                </div>
              </HeroFade>
              <KineticTitle
                testId="cyber-bundle-hero-title"
                className="mt-4 font-serif-d text-[34px] sm:text-5xl lg:text-[54px] leading-[1.06] text-white"
                lines={["The Complete Cyber Fraud", "& Scam Safety Series"]}
              />
              <HeroFade delay={0.25}>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1 text-[13px] sm:text-[14px] uppercase tracking-[0.1em] font-bold text-white/80">
                  <span>3 Digital Books</span>
                  <span>·</span>
                  <span>131 PDF Pages</span>
                </div>
                <div className="mt-6 flex items-center gap-2 flex-wrap">
                  {["PAUSE", "VERIFY", "REPORT"].map((word, i) => (
                    <span key={word} className="flex items-center gap-2">
                      <span className="text-[13px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-sm border" style={{ color: word === "REPORT" ? C.red : C.gold, borderColor: word === "REPORT" ? `${C.red}55` : `${C.gold}55` }}>
                        {word}
                      </span>
                      {i < 2 && <ArrowRight size={14} className="text-white/40" />}
                    </span>
                  ))}
                </div>
                <p className="mt-6 text-[16px] sm:text-[17px] text-white/80 leading-[1.7] max-w-xl">{CYBER_BUNDLE.description}</p>
              </HeroFade>
              <HeroFade delay={0.4}>
                <div className="mt-8 flex items-baseline gap-2">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold">{CYBER_BUNDLE.regular_price_label}</span>
                  <span className="text-[17px] text-white/40 line-through" style={{ textDecorationColor: `${C.gold}B3` }}>{CYBER_BUNDLE.regular_price}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-end gap-4">
                  <div className="font-serif-d text-[48px] leading-none" style={{ color: C.gold }}>{CYBER_BUNDLE.price}</div>
                  <div data-testid="cyber-bundle-page-savings-pill" className="pb-2 text-[12px] uppercase tracking-[0.14em] font-bold rounded-sm px-2.5 py-1" style={{ color: C.navy, backgroundColor: C.gold }}>
                    Save {CYBER_BUNDLE.savings}
                  </div>
                </div>
                <p className="mt-2 text-[12px] uppercase tracking-[0.14em] text-white/60 font-semibold">{CYBER_BUNDLE.offer_label} · {CYBER_BUNDLE.format}</p>
                <div className="mt-6">
                  <BuyButton
                    product={CYBER_BUNDLE}
                    label={`Get All 3 Books — ${CYBER_BUNDLE.price}`}
                    source="bundle-page-hero"
                    testId="bundle-buy-button"
                    dark
                    accentColor={C.gold}
                  />
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-3 text-[13px]">
                  <span className="text-white/40 uppercase tracking-wide text-[11px] font-semibold">Printed Edition · Planned</span>
                  <PrintInterestButton
                    mode="single"
                    seriesId="cyber-safety"
                    productId={CYBER_BUNDLE.product_id}
                    bookTitle={CYBER_BUNDLE.title}
                    sourcePage="cyber-safety-bundle-pdp"
                    testId="bundle-print-interest"
                    accentColor={C.gold}
                    className="text-white/70 hover:text-white underline underline-offset-2 text-[13px] font-semibold transition-colors"
                  />
                </div>
              </HeroFade>
            </div>
            <div className="lg:col-span-5">
              <HeroFade delay={0.3} className="grid grid-cols-3 gap-4 max-w-[440px] mx-auto">
                {CYBER_BOOKS.map((b) => <BookCover key={b.slug} book={b} tilt testId={`cyber-bundle-cover-${b.slug}`} />)}
              </HeroFade>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section style={{ backgroundColor: C.cream }}>
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-28">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.teal }}>What's included</div>
            <h2 className="mt-4 font-serif-d text-[28px] sm:text-4xl leading-[1.1]" style={{ color: C.navy }}>All 3 books, one purchase.</h2>
          </Reveal>
          <div className="mt-12 space-y-6" data-testid="cyber-bundle-books-list">
            {CYBER_BOOKS.map((b) => (
              <Reveal key={b.slug} delay={b.book_number * 0.06}>
                <div className="grid grid-cols-12 gap-6 items-center border rounded-sm p-6" style={{ borderColor: `${C.navy}14`, backgroundColor: "white" }}>
                  <div className="col-span-3 sm:col-span-2">
                    <Link to={`/books/cyber-safety/${b.slug}`}>
                      <BookCover book={b} className="max-w-[110px]" />
                    </Link>
                  </div>
                  <div className="col-span-9 sm:col-span-7">
                    <div className="text-[11px] uppercase tracking-[0.16em] font-semibold" style={{ color: C.gold }}>{b.label} · {b.progression_label}</div>
                    <Link to={`/books/cyber-safety/${b.slug}`} className="mt-1 block font-serif-d text-[19px] hover:underline" style={{ color: C.navy }}>{b.title}</Link>
                    <p className="mt-1 text-[13px]" style={{ color: C.textMuted }}>{b.pages} pages</p>
                  </div>
                  <div className="col-span-12 sm:col-span-3 text-right">
                    <span className="text-[13px] line-through" style={{ color: "#9AA1AB" }}>{b.regular_price}</span>
                    <span className="ml-2 text-[13px] font-semibold" style={{ color: C.textMuted }}>individually {b.price}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-[14px] text-center" style={{ color: C.textMuted }}>
              Buy separately for ₹49 + ₹49 + ₹49 = ₹147, or get the complete series for {CYBER_BUNDLE.price} and save {CYBER_BUNDLE.savings} on the {CYBER_BUNDLE.regular_price_label.toLowerCase()} of {CYBER_BUNDLE.regular_price}.
            </p>
          </Reveal>
        </div>
      </section>

      <StickyBuyBar
        product={CYBER_BUNDLE}
        label={`Get All 3 — ${CYBER_BUNDLE.price}`}
        source="bundle-page-sticky"
        testId="bundle-sticky-buy"
        accentColor={C.gold}
        barClassName="border-[#2A3C60]"
      />
    </main>
  );
}
